import type {
  AircraftState, AlertDraft, AlertRule, AlertRuleSpec, AlertSeverity, AlertTier, EpochMs, FlightCategory, GeneralWeather, Id, LatLon, MetarReport, MissionRecord,
  RunSheetItem, SunEventName, TargetRef, WorldState,
} from './types.ts';
import { angleDiffDeg, clockPosition, distanceM, ftToM, M_PER_FT, M_PER_NM, MPS_PER_KT, relativeGeometry, type Kinematic, type RelativeGeometry } from './geo.ts';
import { sunEventTime, sunPosition, sunTimes } from './sun.ts';
import { formatAltitude, formatBearing, formatLocal, formatUtc } from './format.ts';
import { alertTierForRule, calloutFor, compassWord, numberToWords, spokenDuration, spokenName, type CalloutTrend } from './callout.ts';
import { isAirbornePhase, isGroundPhase } from './phase.ts';
import { categoryChangePlain } from './plainwx.ts';

/**
 * Evaluator memory between ticks. Serializable (plain JSON) so the server can persist/restore it.
 * Keys are `${ruleId}|${subjectKey}`.
 */
export interface AlertEngineState {
  /** Whether the condition was active on the previous tick (for edge detection + hysteresis). */
  active: Record<string, boolean>;
  /** Last time each key fired. */
  lastFired: Record<string, EpochMs>;
  /** Last seen values for change detection (e.g. flight category per station). */
  memo: Record<string, string | number | null>;
}

export function emptyAlertState(): AlertEngineState {
  return { active: {}, lastFired: {}, memo: {} };
}

export interface AlertContext {
  /** Mission configuration + (at least recent) telemetry; used for labels & targets. */
  record: MissionRecord;
  /** Current world (the evaluator only uses state at `world.t`). */
  world: WorldState;
}

/** Proximity re-arms beyond threshold × this. */
export const PROXIMITY_REARM_FACTOR = 1.2;
/** ETA re-arms beyond threshold × this (or when the aircraft is opening). */
export const ETA_REARM_FACTOR = 1.5;
/** Separation 'inside' re-arms beyond ×1.2; 'outside' re-arms inside ÷1.2. */
export const SEPARATION_REARM_FACTOR = 1.2;
/** A sun alert is not delivered if the evaluator first notices the crossing later than this. */
export const SUN_CATCH_UP_MS = 5 * 60_000;

/** WMO weather codes that mean precipitation (drizzle, rain, snow, showers, thunderstorms). */
export function isPrecipWeatherCode(code: number | null): boolean {
  if (code == null) return false;
  return (code >= 51 && code <= 67) || (code >= 71 && code <= 77) || (code >= 80 && code <= 86) || (code >= 95 && code <= 99);
}

/** Is the general weather reporting precipitation now? */
export function isPrecipitating(g: GeneralWeather): boolean {
  return (g.precipMm ?? 0) > 0 || isPrecipWeatherCode(g.weatherCode);
}

// ───────────────────────────── helpers ─────────────────────────────

/** "4.9" below 10 NM, "12" above. */
function nmText(m: number): string {
  const nm = m / M_PER_NM;
  return nm < 9.95 ? nm.toFixed(1) : String(Math.round(nm));
}

/** "IN ~2 MIN" / "IN UNDER 1 MIN" (plain words; "~" = estimate). */
function etaTitle(sec: number): string {
  if (sec < 45) return 'IN UNDER 1 MIN';
  return `IN ~${Math.max(1, Math.round(sec / 60))} MIN`;
}

function etaBody(sec: number): string {
  if (sec < 60) return `~${Math.max(1, Math.round(sec / 5) * 5)} s`;
  return `~${Math.round(sec / 60)} min`;
}

function staleAgeText(sec: number): string {
  return sec < 120 ? `${Math.round(sec)} s` : `${Math.round(sec / 60)} min`;
}

interface ResolvedTarget {
  kin: Kinematic;
  label: string;
  /** Facing reference for clock positions in callouts (camera heading), or null → compass word. */
  facingDeg: number | null;
}

/** Horizontal distance that reads OVERHEAD (spec §5.2: within 500 ft). */
export const OVERHEAD_M = 500 * M_PER_FT;
/** Below this groundspeed an aircraft reads HOLDING (hover / stationary). */
export const HOLDING_MAX_GS_KT = 15;

function aircraftKinematic(s: AircraftState): Kinematic {
  return { pos: { lat: s.lat, lon: s.lon }, altM: s.altFt != null ? ftToM(s.altFt) : null, gsKt: s.gsKt, trackDeg: s.trackDeg, vsFpm: s.vsFpm };
}

/** Activity thresholds: parked/taxiing aircraft are ignored by proximity, ETA and separation rules. */
export const ACTIVE_MIN_GS_KT = 20;
export const ACTIVE_MIN_VS_FPM = 200;
export const ACTIVE_MIN_HEIGHT_FT = 200;

/** Memo key holding the lowest altitude (ft) seen for an aircraft — its ground baseline. */
export const minAltMemoKey = (aircraftId: Id) => `ac:${aircraftId}:minAltFt`;

/**
 * true when an aircraft is doing something worth alerting on: gs ≥ 20 kt, or |vs| ≥ 200 fpm, or
 * ≥ 200 ft above `minAltFt` (the lowest altitude seen, e.g. from `state.memo[minAltMemoKey(id)]`).
 * Hovering after takeoff stays active via height; parked/taxiing aircraft are inactive.
 * The UI can use `!isAircraftActive(...)` to label "ON GROUND / INACTIVE".
 */
export function isAircraftActive(
  s: Pick<AircraftState, 'gsKt' | 'vsFpm' | 'altFt'>,
  minAltFt: number | null | undefined,
): boolean {
  if (s.gsKt != null && s.gsKt >= ACTIVE_MIN_GS_KT) return true;
  if (s.vsFpm != null && Math.abs(s.vsFpm) >= ACTIVE_MIN_VS_FPM) return true;
  return s.altFt != null && minAltFt != null && s.altFt - minAltFt >= ACTIVE_MIN_HEIGHT_FT;
}

const usable = (s: AircraftState | undefined): s is AircraftState => !!s && s.freshness !== 'stale' && s.freshness !== 'lost' && s.freshness !== 'none';

function stationary(pos: LatLon, altM: number | null): Kinematic {
  return { pos, altM, gsKt: 0, trackDeg: null };
}

/** Heading of the first camera this crew member operates (its world state), for callout clock positions. */
function crewFacing(ctx: AlertContext, userId: Id): number | null {
  for (const cam of ctx.record.cameras) {
    if (cam.operatorUserId !== userId) continue;
    const st = ctx.world.cameras.find((c) => c.cameraId === cam.id);
    const h = st?.headingDeg ?? cam.headingDeg;
    if (h != null) return h;
  }
  return null;
}

function resolveTarget(target: TargetRef, ctx: AlertContext): ResolvedTarget | null {
  const { record, world } = ctx;
  const label = targetLabel(record, target);
  switch (target.kind) {
    case 'crew': {
      const c = world.crew.find((x) => x.userId === target.id);
      // Crew positions older than 5 min are 'stale' (spec §6) and no longer drive alerts.
      if (!c || c.freshness === 'lost' || c.freshness === 'none' || c.freshness === 'stale') return null;
      const moving = c.speedMps != null && c.speedMps > 0.5 && c.headingDeg != null;
      return {
        label,
        kin: { pos: { lat: c.lat, lon: c.lon }, altM: null, gsKt: moving ? c.speedMps! / MPS_PER_KT : 0, trackDeg: moving ? c.headingDeg : null },
        facingDeg: crewFacing(ctx, target.id),
      };
    }
    case 'camera': {
      const c = world.cameras.find((x) => x.cameraId === target.id);
      if (!c || !c.position || c.freshness === 'lost') return null;
      return { label, kin: stationary(c.position, null), facingDeg: c.headingDeg };
    }
    case 'location': {
      const l = record.locations.find((x) => x.id === target.id);
      if (!l) return null;
      return { label, kin: stationary({ lat: l.lat, lon: l.lon }, l.elevM), facingDeg: null };
    }
    case 'aircraft': {
      const a = world.aircraft.find((x) => x.aircraftId === target.id);
      if (!usable(a)) return null;
      return { label, kin: aircraftKinematic(a), facingDeg: a.trackDeg };
    }
    case 'airport': {
      const ap = record.airports.find((x) => x.icao.toUpperCase() === target.id.toUpperCase());
      if (!ap) return null;
      return { label, kin: stationary({ lat: ap.lat, lon: ap.lon }, ftToM(ap.elevFt)), facingDeg: null };
    }
    default:
      return null;
  }
}

function selectAircraft(sel: string, ctx: AlertContext): AircraftState[] {
  const states = ctx.world.aircraft;
  return sel === 'any' ? states : states.filter((s) => s.aircraftId === sel);
}

function aircraftLabel(record: Pick<MissionRecord, 'aircraft'>, id: Id): string {
  return record.aircraft.find((a) => a.id === id)?.label ?? id.toUpperCase();
}

const targetKey = (t: TargetRef) => `${t.kind}:${t.id}`;

/** Spoken aircraft name for callouts ("HERON H01" → "Heron"). */
function spokenAircraft(record: Pick<MissionRecord, 'aircraft'>, id: Id): string {
  return spokenName(aircraftLabel(record, id));
}

/** Trend word for callouts from the target's point of view. */
function calloutTrend(g: RelativeGeometry, ac: AircraftState): CalloutTrend | null {
  if (g.distanceM <= OVERHEAD_M) return 'overhead';
  if (ac.gsKt != null && ac.gsKt < HOLDING_MAX_GS_KT) return 'holding';
  if (g.trend === 'closing') return 'inbound';
  if (g.trend === 'opening') return 'outbound';
  return null;
}

/** Clock position relative to the target's facing, else a compass word (never both). */
function direction(g: RelativeGeometry, facingDeg: number | null): { clock: number | null; compass: string | null } {
  if (facingDeg != null) return { clock: clockPosition(angleDiffDeg(facingDeg, g.bearingDeg)), compass: null };
  return { clock: null, compass: compassWord(g.bearingDeg) };
}

/** Lead for run-sheet countdowns: "1 MIN" / "10 S". */
function leadText(sec: number): string {
  return sec >= 60 && sec % 60 === 0 ? `${sec / 60} MIN` : `${sec} S`;
}

/** Mission window used by crew-position rules: [actualStart ?? windowStart, actualEnd ?? windowEnd]. */
function inMissionWindow(record: Pick<MissionRecord, 'mission'>, t: EpochMs): boolean {
  const m = record.mission;
  const from = m.actualStart ?? m.windowStart;
  const to = m.actualEnd ?? m.windowEnd;
  return t >= from && t <= to;
}

// ───────────────────────────── evaluator ─────────────────────────────

class Evaluator {
  readonly drafts: AlertDraft[] = [];
  readonly state: AlertEngineState;
  readonly now: EpochMs;

  constructor(prev: AlertEngineState, readonly ctx: AlertContext) {
    this.state = {
      active: { ...(prev?.active ?? {}) },
      lastFired: { ...(prev?.lastFired ?? {}) },
      memo: { ...(prev?.memo ?? {}) },
    };
    this.now = ctx.world.t;
  }

  /** Update each aircraft's ground baseline (lowest altitude seen) in the memo. */
  trackBaselines(): void {
    for (const a of this.ctx.world.aircraft) {
      if (a.altFt == null || a.freshness === 'lost' || a.freshness === 'none') continue;
      const k = minAltMemoKey(a.aircraftId);
      const prev = this.state.memo[k];
      if (typeof prev !== 'number' || a.altFt < prev) this.state.memo[k] = a.altFt;
    }
  }

  active(a: AircraftState): boolean {
    const m = this.state.memo[minAltMemoKey(a.aircraftId)];
    return isAircraftActive(a, typeof m === 'number' ? m : null);
  }

  /** Edge detector with hysteresis. Returns true on a rising edge. */
  edge(key: string, conditionNow: boolean, rearmNow: boolean): boolean {
    const was = this.state.active[key] === true;
    if (!was && conditionNow) {
      this.state.active[key] = true;
      return true;
    }
    if (was && rearmNow && !conditionNow) this.state.active[key] = false;
    return false;
  }

  /** Emit a draft unless the key is inside its cooldown. Every draft carries its tier and callout. */
  fire(
    rule: AlertRule,
    subjectKey: string,
    title: string,
    body: string,
    subjects: TargetRef[],
    opts: { preserveCase?: boolean; callout?: string | null; tier?: AlertTier; severity?: AlertSeverity } = {},
  ): void {
    const dedupeKey = `${rule.id}|${subjectKey}`;
    const last = this.state.lastFired[dedupeKey];
    if (last != null && this.now >= last && this.now - last < rule.cooldownSec * 1000) return;
    this.state.lastFired[dedupeKey] = this.now;
    this.drafts.push({
      ruleId: rule.id,
      t: this.now,
      severity: opts.severity ?? rule.severity,
      title: opts.preserveCase ? title : title.toUpperCase(),
      body,
      dedupeKey,
      subjects,
      tier: opts.tier ?? alertTierForRule(rule.spec.type),
      callout: opts.callout ?? null,
    });
  }

  run(rule: AlertRule): void {
    const spec = rule.spec;
    switch (spec.type) {
      case 'proximity': return this.proximity(rule, spec);
      case 'eta': return this.eta(rule, spec);
      case 'sun': return this.sun(rule, spec);
      case 'weather_change': return this.weather(rule, spec);
      case 'stale_data': return this.stale(rule, spec);
      case 'separation': return this.separation(rule, spec);
      case 'cpa': return this.cpa(rule, spec);
      case 'phase_change': return this.phaseChange(rule, spec);
      case 'runsheet_due': return this.runsheetDue(rule, spec);
      case 'crew_off_position': return this.crewOffPosition(rule, spec);
      default: return;
    }
  }

  proximity(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'proximity' }>): void {
    const target = resolveTarget(spec.target, this.ctx);
    if (!target) return;
    for (const ac of selectAircraft(spec.aircraft, this.ctx)) {
      if (!usable(ac) || !this.active(ac)) continue; // inactive: no state change
      if (spec.target.kind === 'aircraft' && spec.target.id === ac.aircraftId) continue;
      const g = relativeGeometry(target.kin, aircraftKinematic(ac));
      const thrM = spec.thresholdNm * M_PER_NM;
      const subjectKey = `${ac.aircraftId}>${targetKey(spec.target)}`;
      const key = `${rule.id}|${subjectKey}`;
      if (!this.edge(key, g.distanceM <= thrM, g.distanceM > thrM * PROXIMITY_REARM_FACTOR)) continue;
      const acLabel = aircraftLabel(this.ctx.record, ac.aircraftId);
      const parts = [`${nmText(g.distanceM)} NM`, `bearing ${formatBearing(g.bearingDeg)} from ${target.label}`];
      if (ac.altFt != null) parts.push(formatAltitude(ac.altFt));
      if (g.trend === 'closing' && g.closingKt != null) parts.push(`closing ${Math.round(g.closingKt)} kt`);
      else if (g.trend === 'opening') parts.push('opening');
      if (g.etaSec != null) parts.push(`ETA ${etaBody(g.etaSec)} (estimated)`);
      const callout = calloutFor({ who: spokenAircraft(this.ctx.record, ac.aircraftId), ...direction(g, target.facingDeg), distanceM: g.distanceM, trend: calloutTrend(g, ac) });
      this.fire(rule, subjectKey, `${acLabel} — ${nmText(g.distanceM)} NM FROM ${target.label}`, parts.join(' · '), [
        { kind: 'aircraft', id: ac.aircraftId },
        spec.target,
      ], { callout });
    }
  }

  eta(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'eta' }>): void {
    const target = resolveTarget(spec.target, this.ctx);
    if (!target) return;
    for (const ac of selectAircraft(spec.aircraft, this.ctx)) {
      if (!usable(ac) || !this.active(ac)) continue; // inactive: no state change
      if (spec.target.kind === 'aircraft' && spec.target.id === ac.aircraftId) continue;
      const g = relativeGeometry(target.kin, aircraftKinematic(ac));
      const subjectKey = `${ac.aircraftId}>${targetKey(spec.target)}:eta`;
      const key = `${rule.id}|${subjectKey}`;
      const cond = g.etaSec != null && g.etaSec <= spec.thresholdSec;
      const rearm = g.trend === 'opening' || (g.etaSec != null && g.etaSec > spec.thresholdSec * ETA_REARM_FACTOR);
      if (!this.edge(key, cond, rearm)) continue;
      const acLabel = aircraftLabel(this.ctx.record, ac.aircraftId);
      const parts = [`ETA ${etaBody(g.etaSec!)} (estimated)`, `${nmText(g.distanceM)} NM`, `bearing ${formatBearing(g.bearingDeg)} from ${target.label}`];
      if (ac.altFt != null) parts.push(formatAltitude(ac.altFt));
      if (g.closingKt != null) parts.push(`closing ${Math.round(g.closingKt)} kt`);
      const callout = calloutFor({
        who: spokenAircraft(this.ctx.record, ac.aircraftId), ...direction(g, target.facingDeg), distanceM: g.distanceM,
        event: `${spokenName(target.label, { keepSerial: true }).toLowerCase()} in ${spokenDuration(Math.round(g.etaSec! / 5) * 5 || 5)}`,
      });
      this.fire(rule, subjectKey, `${acLabel} — REACHES ${target.label} ${etaTitle(g.etaSec!)}`, parts.join(' · '), [
        { kind: 'aircraft', id: ac.aircraftId },
        spec.target,
      ], { callout });
    }
  }

  sun(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'sun' }>): void {
    const m = this.ctx.record.mission;
    const lastKey = `${rule.id}|sun:lastT`;
    const lastT = this.state.memo[lastKey];
    this.state.memo[lastKey] = this.now;
    if (typeof lastT !== 'number' || this.now <= lastT) return;
    // Check the local day of the previous tick and of now (handles midnight roll-over).
    const days = new Map<string, ReturnType<typeof sunTimes>>();
    for (const t of [lastT, this.now]) {
      const st = sunTimes(t, m.center.lat, m.center.lon, m.timezone);
      days.set(st.date, st);
    }
    for (const st of days.values()) {
      const ev = sunEventTime(st, spec.event);
      if (ev == null) continue;
      const fireAt = ev - spec.leadMin * 60_000;
      if (!(lastT < fireAt && fireAt <= this.now) || this.now - fireAt > SUN_CATCH_UP_MS) continue;
      const subjectKey = `sun:${spec.event}:${st.date}`;
      const key = `${rule.id}|${subjectKey}`;
      if (this.state.active[key]) continue;
      this.state.active[key] = true;
      const mins = Math.round((ev - this.now) / 60_000);
      const name = SUN_EVENT_TITLES[spec.event];
      const title = mins <= 0 ? `${name} NOW` : `${name} IN ${mins} ${mins === 1 ? 'MINUTE' : 'MINUTES'}`;
      const pos = sunPosition(ev, m.center.lat, m.center.lon);
      const body = `${SUN_EVENT_NAMES[spec.event]} at ${formatLocal(ev, m.timezone, false)} (${formatUtc(ev, false)}) · sun az ${formatBearing(pos.azimuthDeg)}, ${Math.round(pos.elevationDeg)}° elevation`;
      const callout = calloutFor({ who: SUN_EVENT_NAMES[spec.event], event: mins <= 0 ? 'now' : null, inSec: mins > 0 ? mins * 60 : null });
      this.fire(rule, subjectKey, title, body, [], { callout });
    }
  }

  weather(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'weather_change' }>): void {
    const { world, record } = this.ctx;
    const metars = spec.stationId == null
      ? world.metars
      : world.metars.filter((r) => r.stationId.toUpperCase() === spec.stationId!.toUpperCase());
    for (const r of metars) this.weatherStation(rule, spec, r, record);

    if (spec.stationId == null && spec.precipStart && world.general) {
      const key = `${rule.id}|wx:general:precip`;
      const now = isPrecipitating(world.general) ? 1 : 0;
      const prev = this.state.memo[key];
      this.state.memo[key] = now;
      if (typeof prev === 'number' && prev === 0 && now === 1) {
        const g = world.general;
        const parts = [g.weatherText ?? 'Precipitation reported'];
        if (g.precipMm != null && g.precipMm > 0) parts.push(`${g.precipMm.toFixed(1)} mm/h`);
        if (g.precipProbPct != null) parts.push(`${Math.round(g.precipProbPct)}% chance`);
        parts.push('mission area (model)');
        this.fire(rule, 'wx:general:precip', 'WEATHER — PRECIPITATION STARTING', parts.join(' · '), [], { callout: 'Weather, precipitation starting.' });
      }
    }
  }

  weatherStation(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'weather_change' }>, r: MetarReport, record: MissionRecord): void {
    const s = r.stationId.toUpperCase();
    const subjects: TargetRef[] = record.airports.some((a) => a.icao.toUpperCase() === s) ? [{ kind: 'airport', id: s }] : [];
    const detail = metarDetail(r);

    // Flight category change (first observation seeds the memo).
    const catKey = `${rule.id}|wx:${s}:cat`;
    const prevCat = this.state.memo[catKey];
    if (r.flightCategory !== 'UNKNOWN') {
      this.state.memo[catKey] = r.flightCategory;
      if (spec.onFlightCategoryChange && typeof prevCat === 'string' && prevCat !== r.flightCategory) {
        // A category change alone cannot identify the changing measurement (clouds or visibility).
        const plain = categoryChangePlain(prevCat as FlightCategory, r);
        this.fire(rule, `wx:${s}:cat>${r.flightCategory}`, `WEATHER — ${s} ${plain.toUpperCase()}`, `${detail} · ${prevCat} → ${r.flightCategory}`, subjects, {
          callout: `Weather, ${plain.charAt(0).toLowerCase()}${plain.slice(1).replace(':', ',').replace(/~/g, 'about ').replace(/ ft\b/, ' feet').replace(/\b(?:MVFR|LIFR|VFR|IFR)\b/g, (category) => category.split('').join(' '))}.`,
        });
      }
    }

    // Wind increase vs. baseline. The baseline follows the wind down (so a build from a lull is
    // caught) and resets to the new value after each alert.
    if (r.windKt != null) {
      const baseKey = `${rule.id}|wx:${s}:windBase`;
      const base = this.state.memo[baseKey];
      if (typeof base !== 'number') this.state.memo[baseKey] = r.windKt;
      else if (r.windKt < base) this.state.memo[baseKey] = r.windKt;
      else if (spec.windIncreaseKt > 0 && r.windKt - base >= spec.windIncreaseKt) {
        this.state.memo[baseKey] = r.windKt;
        this.fire(rule, `wx:${s}:wind`, `WEATHER — ${s} WIND ${Math.round(r.windKt)} KT (WAS ${Math.round(base)} KT)`, detail, subjects, {
          callout: calloutFor({ who: 'Wind', event: `${spokenKnots(r.windKt)}, was ${spokenKnots(base)}` }),
        });
      }
    }

    // Gusts at/above threshold (edge; re-arms when gusts drop below).
    if (spec.gustKt > 0) {
      const gust = r.gustKt ?? 0;
      if (this.edge(`${rule.id}|wx:${s}:gust`, gust >= spec.gustKt, gust < spec.gustKt)) {
        this.fire(rule, `wx:${s}:gust`, `WEATHER — ${s} GUSTS ${Math.round(gust)} KT`, detail, subjects, {
          callout: calloutFor({ who: 'Wind', event: `gusts ${spokenKnots(gust)}` }),
        });
      }
    }
  }

  stale(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'stale_data' }>): void {
    const ids = spec.aircraft === 'any' ? this.ctx.record.aircraft.map((a) => a.id) : [spec.aircraft];
    for (const id of ids) {
      const s = this.ctx.world.aircraft.find((a) => a.aircraftId === id);
      if (!s) continue; // never had data — nothing to go stale
      const subjectKey = `${id}:stale`;
      const key = `${rule.id}|${subjectKey}`;
      const wasStale = this.state.active[key] === true;
      if (!this.edge(key, s.ageSec >= spec.thresholdSec, s.ageSec < spec.thresholdSec)) {
        // Position back after a stale alert: a quiet info line so the list doesn't end on "lost".
        if (wasStale && this.state.active[key] === false) {
          const label = aircraftLabel(this.ctx.record, id);
          this.fire(rule, `${id}:back`, `${label.toUpperCase()} — POSITION BACK`, `Live position again (via ${s.source})`, [{ kind: 'aircraft', id }], {
            preserveCase: true,
            tier: 3,
            severity: 'info',
            callout: calloutFor({ who: spokenName(label), event: 'position back' }),
          });
        }
        continue;
      }
      const label = aircraftLabel(this.ctx.record, id);
      const body = `No position for ${staleAgeText(s.ageSec)} (last ${formatUtc(s.lastObservedT)} via ${s.source}) · showing last known position`;
      // Unit stays lower-case per the contract example "HERON H01 — DATA STALE (45 s)".
      this.fire(rule, subjectKey, `${label.toUpperCase()} — DATA STALE (${staleAgeText(s.ageSec)})`, body, [{ kind: 'aircraft', id }], {
        preserveCase: true,
        callout: calloutFor({ who: spokenName(label), event: 'position stale' }),
      });
    }
  }

  separation(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'separation' }>): void {
    const a = this.ctx.world.aircraft.find((x) => x.aircraftId === spec.aircraftA);
    const b = this.ctx.world.aircraft.find((x) => x.aircraftId === spec.aircraftB);
    if (!usable(a) || !usable(b) || !this.active(a) || !this.active(b)) return;
    const g = relativeGeometry(aircraftKinematic(a), aircraftKinematic(b));
    const thrM = spec.thresholdNm * M_PER_NM;
    const inside = spec.direction === 'inside';
    const cond = inside ? g.distanceM <= thrM : g.distanceM > thrM;
    const rearm = inside ? g.distanceM > thrM * SEPARATION_REARM_FACTOR : g.distanceM < thrM / SEPARATION_REARM_FACTOR;
    const subjectKey = `${spec.aircraftA}~${spec.aircraftB}:${spec.direction}`;
    if (!this.edge(`${rule.id}|${subjectKey}`, cond, rearm)) return;
    const la = aircraftLabel(this.ctx.record, spec.aircraftA);
    const lb = aircraftLabel(this.ctx.record, spec.aircraftB);
    const thr = spec.thresholdNm.toFixed(1);
    const title = `${la} / ${lb} — ${nmText(g.distanceM)} NM APART (${inside ? 'WITHIN' : 'BEYOND'} ${thr} NM)`;
    const parts = [`${lb} bears ${formatBearing(g.bearingDeg)} from ${la}`];
    if (g.clock != null) parts.push(`${g.clock} o'clock`);
    if (g.altDiffFt != null) parts.push(`${g.altDiffFt >= 0 ? '+' : '−'}${Math.abs(Math.round(g.altDiffFt))} ft`);
    if (g.trend !== 'unknown') parts.push(g.trend);
    this.fire(rule, subjectKey, title, parts.join(' · '), [
      { kind: 'aircraft', id: spec.aircraftA },
      { kind: 'aircraft', id: spec.aircraftB },
    ], { callout: calloutFor({ who: spokenName(lb), clock: g.clock, distanceM: g.distanceM, event: `from ${spokenName(la).toLowerCase()}` }) });
  }

  /** Closest point of approach inside `thresholdSec` and within `maxCpaNm` of the target, while closing. */
  cpa(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'cpa' }>): void {
    const target = resolveTarget(spec.target, this.ctx);
    if (!target) return;
    for (const ac of selectAircraft(spec.aircraft, this.ctx)) {
      if (!usable(ac) || !this.active(ac)) continue;
      if (spec.target.kind === 'aircraft' && spec.target.id === ac.aircraftId) continue;
      const g = relativeGeometry(target.kin, aircraftKinematic(ac));
      const subjectKey = `${ac.aircraftId}>${targetKey(spec.target)}:cpa`;
      const key = `${rule.id}|${subjectKey}`;
      const maxM = spec.maxCpaNm * M_PER_NM;
      // A CPA a few seconds away is "now" — too late to be an action alert.
      const cond = g.trend === 'closing' && g.cpa != null && g.cpa.tSec <= spec.thresholdSec && g.cpa.tSec >= CPA_MIN_LEAD_SEC && g.cpa.distanceM <= maxM;
      const rearm = g.trend === 'opening' || g.cpa == null || g.cpa.tSec > spec.thresholdSec * ETA_REARM_FACTOR;
      if (!this.edge(key, cond, rearm)) continue;
      const cpa = g.cpa!;
      const acLabel = aircraftLabel(this.ctx.record, ac.aircraftId);
      const secs = Math.max(1, Math.round(cpa.tSec));
      const parts = [
        `closest ${formatCpaDistance(cpa.distanceM)} in ~${secs} s (estimated)`,
        `now ${nmText(g.distanceM)} NM, bearing ${formatBearing(g.bearingDeg)} from ${target.label}`,
      ];
      if (ac.altFt != null) parts.push(formatAltitude(ac.altFt));
      if (g.closingKt != null) parts.push(`closing ${Math.round(g.closingKt)} kt`);
      const callout = calloutFor({
        who: spokenAircraft(this.ctx.record, ac.aircraftId), ...direction(g, target.facingDeg), distanceM: cpa.distanceM,
        event: `closest in ${spokenDuration(Math.round(cpa.tSec / 5) * 5 || secs)}`,
      });
      this.fire(rule, subjectKey, `${acLabel} — PASSES ${plainDistance(cpa.distanceM)} FROM ${target.label} IN ~${secs} S`, parts.join(' · '), [
        { kind: 'aircraft', id: ac.aircraftId },
        spec.target,
      ], { callout });
    }
  }

  /** Takeoff (ground → airborne) / landing (airborne → LANDED) from the world state's flight phase. */
  phaseChange(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'phase_change' }>): void {
    for (const ac of selectAircraft(spec.aircraft, this.ctx)) {
      if (!usable(ac) || !ac.phase || ac.phase === 'UNKNOWN') continue;
      const memoKey = `${rule.id}|ac:${ac.aircraftId}:air`;
      const candKey = `${memoKey}:cand`;
      const sinceKey = `${memoKey}:since`;
      const prev = this.state.memo[memoKey];
      const air = isAirbornePhase(ac.phase) ? 'air' : isGroundPhase(ac.phase) ? 'ground' : null;
      if (air == null) continue;
      if (typeof prev !== 'string') {
        this.state.memo[memoKey] = air; // first observation seeds
        continue;
      }
      // Landing needs LANDED (not a taxi at speed); takeoff needs a clear airborne phase.
      if (air === 'ground' && prev === 'air' && ac.phase !== 'LANDED') continue;
      if (prev === air) {
        this.state.memo[candKey] = null;
        continue;
      }
      // Debounce: ADS-B altitude (25 ft steps) cannot tell a low hover from the ground, so a change
      // must hold for PHASE_CONFIRM_SEC before it is announced.
      if (this.state.memo[candKey] !== air) {
        this.state.memo[candKey] = air;
        this.state.memo[sinceKey] = this.now;
      }
      const since = this.state.memo[sinceKey];
      const hold = (air === 'air' ? TAKEOFF_CONFIRM_SEC : LANDING_CONFIRM_SEC) * 1000;
      if (typeof since !== 'number' || this.now - since < hold) continue;
      this.state.memo[memoKey] = air;
      this.state.memo[candKey] = null;
      const event = air === 'air' ? 'takeoff' : 'landing';
      if (!spec.events.includes(event)) continue;
      const label = aircraftLabel(this.ctx.record, ac.aircraftId);
      const est = ac.phaseEstimated !== false ? ' (estimated from track)' : '';
      const body = `${event === 'takeoff' ? 'Airborne' : 'Down'}${est} · ${ac.altFt != null ? formatAltitude(ac.altFt) : 'altitude unknown'} · ${Math.round(ac.gsKt ?? 0)} kt · phase ${ac.phase}`;
      this.fire(rule, `${ac.aircraftId}:${event}:${this.now}`, `${label} — ${event === 'takeoff' ? 'TAKEOFF' : 'LANDING'} DETECTED`, body, [{ kind: 'aircraft', id: ac.aircraftId }], {
        callout: calloutFor({ who: spokenName(label), event: event === 'takeoff' ? 'takeoff' : 'landed' }),
      });
    }
  }

  /** Countdown callouts before each scheduled, not-yet-finished run-sheet item (crossing semantics, like sun events). */
  runsheetDue(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'runsheet_due' }>): void {
    const lastKey = `${rule.id}|rs:lastT`;
    const lastT = this.state.memo[lastKey];
    this.state.memo[lastKey] = this.now;
    if (typeof lastT !== 'number' || this.now <= lastT) return;
    const items = (this.ctx.record.runSheet ?? []).filter((i) => (i.status === 'pending' || i.status === 'active') && i.scheduledT != null);
    for (const item of items) {
      for (const lead of spec.leadSec) {
        const fireAt = item.scheduledT! - lead * 1000;
        if (!(lastT < fireAt && fireAt <= this.now) || this.now - fireAt > RUNSHEET_CATCH_UP_MS) continue;
        const subjectKey = `rs:${item.id}:${lead}:${item.scheduledT}`;
        const key = `${rule.id}|${subjectKey}`;
        if (this.state.active[key]) continue;
        this.state.active[key] = true;
        const body = `Run sheet #${item.order} scheduled ${formatLocal(item.scheduledT!, this.ctx.record.mission.timezone)} (${formatUtc(item.scheduledT!)})${item.notes ? ` · ${item.notes}` : ''}`;
        this.fire(rule, subjectKey, `NEXT — ${runSheetTitle(item)} IN ${leadText(lead)}`, body, [], {
          callout: calloutFor({ who: item.label, inSec: lead }),
        });
      }
    }
  }

  /** A crew member > thresholdM from the planned position of a camera they operate, during the mission window. Fires once; re-arms on return. */
  crewOffPosition(rule: AlertRule, spec: Extract<AlertRuleSpec, { type: 'crew_off_position' }>): void {
    const { record, world } = this.ctx;
    if (!inMissionWindow(record, this.now)) return;
    for (const cam of record.cameras) {
      if (!cam.operatorUserId || !cam.position) continue;
      const c = world.crew.find((x) => x.userId === cam.operatorUserId);
      if (!c || (c.freshness !== 'live' && c.freshness !== 'aging')) continue; // stale crew: sheet only, no alert (§6)
      const d = distanceM({ lat: c.lat, lon: c.lon }, cam.position);
      const subjectKey = `crew:${cam.operatorUserId}>cam:${cam.id}:off`;
      if (!this.edge(`${rule.id}|${subjectKey}`, d > spec.thresholdM, d <= spec.thresholdM * CREW_POSITION_REARM_FACTOR)) continue;
      const who = targetLabel(record, { kind: 'crew', id: cam.operatorUserId });
      this.fire(rule, subjectKey, `${who} OFF ${cam.label.toUpperCase()} POSITION — ${Math.round(d)} m`, `${who} is ${Math.round(d)} m from the planned ${cam.label} position (threshold ${spec.thresholdM} m)`, [
        { kind: 'crew', id: cam.operatorUserId },
        { kind: 'camera', id: cam.id },
      ], { preserveCase: true, callout: calloutFor({ who: spokenName(who, { keepSerial: true }), event: `off ${spokenName(cam.label, { keepSerial: true })} position` }) });
    }
  }
}

/** CPA alerts need at least this much warning (s): "passes 1.1 mi from you in ~7 s" is too late to act on. */
export const CPA_MIN_LEAD_SEC = 10;

/** A takeoff / landing must persist this long before it is announced (s). */
export const TAKEOFF_CONFIRM_SEC = 3;
export const LANDING_CONFIRM_SEC = 10;

/** Run-sheet countdown not delivered if first noticed later than this after its moment. */
export const RUNSHEET_CATCH_UP_MS = 5000;
/** Crew off-position re-arms once back within threshold × this. */
export const CREW_POSITION_REARM_FACTOR = 0.8;

function runSheetTitle(item: Pick<RunSheetItem, 'label'>): string {
  return item.label.toUpperCase();
}

function spokenKnots(kt: number): string {
  return `${calloutNumber(kt)} knots`;
}

function calloutNumber(n: number): string {
  return numberToWords(Math.round(n));
}

/** Crew-facing distance: "880 FT" below 1,000 ft, else statute miles "0.6 MI". */
function plainDistance(m: number): string {
  const ft = m / M_PER_FT;
  if (ft < 1000) return `${Math.max(10, Math.round(ft / 10) * 10)} FT`;
  return `${(m / 1609.344).toFixed(1)} MI`;
}

/** "600 ft" below 6,000 ft, else "1.2 NM". */
function formatCpaDistance(m: number): string {
  const ft = m / M_PER_FT;
  if (ft < 6000) return `${Math.max(10, Math.round(ft / 10) * 10).toLocaleString('en-US')} ft`;
  return `${nmText(m)} NM`;
}

const SUN_EVENT_TITLES: Record<SunEventName, string> = {
  sunrise: 'SUNRISE',
  sunset: 'SUNSET',
  golden_hour_morning_end: 'GOLDEN HOUR ENDS',
  golden_hour_evening_start: 'GOLDEN HOUR',
  civil_dawn: 'CIVIL DAWN',
  civil_dusk: 'CIVIL DUSK',
};

const SUN_EVENT_NAMES: Record<SunEventName, string> = {
  sunrise: 'Sunrise',
  sunset: 'Sunset',
  golden_hour_morning_end: 'Morning golden hour ends',
  golden_hour_evening_start: 'Evening golden hour starts',
  civil_dawn: 'Civil dawn',
  civil_dusk: 'Civil dusk',
};

function metarDetail(r: MetarReport): string {
  const parts: string[] = [];
  if (r.windKt != null) {
    const dir = r.windDirDeg === 'VRB' ? 'VRB' : r.windDirDeg != null ? formatBearing(r.windDirDeg) : '';
    parts.push(`wind ${dir ? `${dir} ` : ''}${Math.round(r.windKt)} kt${r.gustKt != null ? ` G${Math.round(r.gustKt)}` : ''}`);
  }
  if (r.visibilityText != null || r.visibilitySm != null) parts.push(`vis ${r.visibilityText ?? r.visibilitySm} SM`);
  parts.push(r.ceilingFt != null ? `ceiling ${formatAltitude(r.ceilingFt)}` : 'no ceiling');
  if (r.wx) parts.push(r.wx);
  parts.push(`obs ${formatUtc(r.t, false)}`);
  return parts.join(' · ');
}

/**
 * Pure rule evaluation. Fires on rising edges only, with hysteresis
 * (proximity re-arms after the aircraft goes beyond threshold × 1.2; ETA re-arms beyond ×1.5 or when opening),
 * honours per-(rule, subject) cooldowns, and ignores stale/lost aircraft for proximity/eta.
 * Titles follow "HERON H01 — 5 NM FROM JAKE", "HERON H01 — EST. 2 MIN TO CAM SITE B",
 * "SUNSET IN 20 MINUTES", "WEATHER — KCVO CLOUDS GETTING LOWER: NOW ~1,200 FT", "HERON H01 — DATA STALE (45 s)".
 *
 * Proximity / ETA / separation only consider ACTIVE aircraft (`isAircraftActive`, using the
 * per-aircraft ground baseline kept in `state.memo`). An inactive aircraft is skipped without
 * touching its edge state, so a parked aircraft next to crew never fires, and one that takes off
 * inside the threshold fires once, when it becomes active (a rising edge in eligibility).
 *
 * First-evaluation policy: proximity / ETA / separation / stale fire immediately if the condition
 * already holds for an active aircraft; sun alerts fire only when `now` crosses (event − lead) between two evaluations
 * (once per event per local day); weather alerts fire only on a change (first report seeds the memo).
 * The input state is not mutated; a new state object is returned.
 */
export function evaluateAlerts(
  rules: readonly AlertRule[],
  ctx: AlertContext,
  state: AlertEngineState,
): { drafts: AlertDraft[]; state: AlertEngineState } {
  const ev = new Evaluator(state, ctx);
  ev.trackBaselines();
  for (const rule of rules) {
    if (!rule.enabled) continue;
    ev.run(rule);
  }
  return { drafts: ev.drafts, state: ev.state };
}

/**
 * Sensible default rules for a new mission: per subject aircraft — proximity / CPA to each crew
 * member and camera site, ETA to sites, stale data, takeoff/landing; plus sunset, golden hour,
 * weather, run-sheet countdowns (60 s, 10 s), crew off planned position (50 m) and photo-ship separation.
 */
export function defaultAlertRules(record: Pick<MissionRecord, 'mission' | 'members' | 'aircraft' | 'locations'>): Array<Omit<AlertRule, 'id' | 'missionId' | 'createdAt'>> {
  const rules: Array<Omit<AlertRule, 'id' | 'missionId' | 'createdAt'>> = [];
  const subjects = record.aircraft.filter((a) => a.role === 'subject');
  const photoShips = record.aircraft.filter((a) => a.role === 'photo_ship');
  const sites = record.locations.filter((l) => l.kind === 'camera_site');

  for (const s of subjects) {
    for (const m of record.members) {
      rules.push({
        name: `${s.label} within 5 NM of ${m.callsign || m.displayName}`,
        enabled: true,
        severity: 'advisory',
        cooldownSec: 600,
        spec: { type: 'proximity', aircraft: s.id, target: { kind: 'crew', id: m.userId }, thresholdNm: 5 },
      });
    }
    for (const l of sites) {
      rules.push({
        name: `${s.label} within 2 NM of ${l.name}`,
        enabled: true,
        severity: 'advisory',
        cooldownSec: 600,
        spec: { type: 'proximity', aircraft: s.id, target: { kind: 'location', id: l.id }, thresholdNm: 2 },
      });
      rules.push({
        name: `${s.label} 2 min from ${l.name}`,
        enabled: true,
        severity: 'advisory',
        cooldownSec: 600,
        spec: { type: 'eta', aircraft: s.id, target: { kind: 'location', id: l.id }, thresholdSec: 120 },
      });
    }
    rules.push({
      name: `${s.label} data stale`,
      enabled: true,
      severity: 'warning',
      cooldownSec: 300,
      spec: { type: 'stale_data', aircraft: s.id, thresholdSec: 45 },
    });
    for (const m of record.members) {
      rules.push({
        name: `${s.label} CPA within 30 s of ${m.callsign || m.displayName}`,
        enabled: true,
        severity: 'advisory',
        cooldownSec: 120,
        spec: { type: 'cpa', aircraft: s.id, target: { kind: 'crew', id: m.userId }, thresholdSec: 30, maxCpaNm: 1 },
      });
    }
    for (const l of sites) {
      rules.push({
        name: `${s.label} CPA within 30 s of ${l.name}`,
        enabled: true,
        severity: 'advisory',
        cooldownSec: 120,
        spec: { type: 'cpa', aircraft: s.id, target: { kind: 'location', id: l.id }, thresholdSec: 30, maxCpaNm: 1 },
      });
    }
    rules.push({
      name: `${s.label} takeoff / landing`,
      enabled: true,
      severity: 'advisory',
      cooldownSec: 60,
      spec: { type: 'phase_change', aircraft: s.id, events: ['takeoff', 'landing'] },
    });
    // Aviation lane (server-evaluated with the traffic picture / DEM; tier 3 info).
    rules.push({
      name: `Traffic near ${s.label} (3 NM / ±1,000 ft) or the working area`,
      enabled: true,
      severity: 'info',
      cooldownSec: 300,
      spec: { type: 'traffic_near_subject', aircraft: s.id, radiusNm: 3, altBandFt: 1000, workingAreaNm: 1.5 },
    });
    rules.push({
      name: `${s.label} terrain-masked from a camera (predicted 30 s)`,
      enabled: true,
      severity: 'info',
      cooldownSec: 120,
      spec: { type: 'terrain_mask', aircraft: s.id, horizonSec: 30 },
    });
  }

  rules.push({ name: 'Run sheet — 60 s and 10 s', enabled: true, severity: 'advisory', cooldownSec: 0, spec: { type: 'runsheet_due', leadSec: [60, 10] } });
  rules.push({ name: 'Crew off planned position (50 m)', enabled: true, severity: 'info', cooldownSec: 300, spec: { type: 'crew_off_position', thresholdM: 50 } });

  rules.push({ name: 'Sunset in 20 minutes', enabled: true, severity: 'info', cooldownSec: 3600, spec: { type: 'sun', event: 'sunset', leadMin: 20 } });
  rules.push({
    name: 'Golden hour in 10 minutes',
    enabled: true,
    severity: 'info',
    cooldownSec: 3600,
    spec: { type: 'sun', event: 'golden_hour_evening_start', leadMin: 10 },
  });
  rules.push({
    name: 'Weather change',
    enabled: true,
    severity: 'advisory',
    cooldownSec: 600,
    spec: { type: 'weather_change', stationId: null, windIncreaseKt: 10, gustKt: 25, onFlightCategoryChange: true, precipStart: true },
  });

  for (const s of subjects) {
    for (const p of photoShips) {
      rules.push({
        name: `${p.label} more than 1.0 NM from ${s.label}`,
        enabled: true,
        severity: 'advisory',
        cooldownSec: 300,
        spec: { type: 'separation', aircraftA: s.id, aircraftB: p.id, thresholdNm: 1, direction: 'outside' },
      });
    }
  }
  return rules;
}

/** Human-readable name for any target, e.g. "JAKE", "CAM SITE B", "KCVO". */
export function targetLabel(record: Pick<MissionRecord, 'members' | 'aircraft' | 'cameras' | 'locations' | 'airports'>, target: { kind: string; id: Id }): string {
  switch (target.kind) {
    case 'crew': {
      const m = record.members.find((x) => x.userId === target.id);
      return (m?.callsign || m?.displayName || 'CREW').toUpperCase();
    }
    case 'camera':
      return (record.cameras.find((x) => x.id === target.id)?.label ?? 'CAMERA').toUpperCase();
    case 'location':
      return (record.locations.find((x) => x.id === target.id)?.name ?? 'LOCATION').toUpperCase();
    case 'aircraft':
      return (record.aircraft.find((x) => x.id === target.id)?.label ?? 'AIRCRAFT').toUpperCase();
    case 'airport':
      return target.id.toUpperCase();
    default:
      return String(target.id).toUpperCase();
  }
}
